import { type FC } from 'react';
import { CpfInput } from '@/app/components/inputs/CpfInput';
import { CnpjInput } from '@/app/components/inputs/CnpjInput';
import { BRAZILIAN_STATES } from '../../../consts/States';
import { NumericFormat, PatternFormat } from 'react-number-format';
import Select from 'react-select';
import { type FormikProps, getIn } from 'formik';
import { taxRegimeOptions } from '../../../enums/TaxRegime';
import { incomeTaxRegimeOptions, usesIncomeTaxRegime } from '../../../enums/IncomeTaxRegime';
import { type SetupRequestDto } from '../../../models/SetupRequestDto';
import MetronicCard from '@/app/components/layout/MetronicCard';

type Props = {
  formik: FormikProps<SetupRequestDto>;
};

const personTypeOptions = [
  { value: 'LEGAL', label: 'Pessoa jurídica' },
  { value: 'PHYSICAL', label: 'Pessoa física' },
];

const Step1: FC<Props> = ({ formik }) => {
  return (
    <div className="d-flex flex-column">
      <div className="d-flex align-items-center mb-6">
        <h3 className="stepper-title me-7">Dados da sua empresa</h3>
        <div className="stepper-desc opacity-50">Informações sobre sua empresa</div>
      </div>
      <MetronicCard className="h-auto w-100">
        <div className="card-body p-4 p-lg-6">
          <div className="row g-6 mb-12">
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Razão social</h3>
              <input
                className="form-control"
                id="social_name"
                placeholder="Razão social"
                type="text"
                {...formik.getFieldProps('social_name')}
              />
              {!!formik.errors.social_name && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.social_name}</div>
                </div>
              )}
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Nome fantasia</h3>
              <input
                className="form-control"
                id="fantasy_name"
                placeholder="Nome fantasia"
                type="text"
                {...formik.getFieldProps('fantasy_name')}
              />
              {!!formik.errors.fantasy_name && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.fantasy_name}</div>
                </div>
              )}
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Tipo de pessoa</h3>
              <Select
                className="react-select-styled"
                classNamePrefix="react-select"
                id="person_type"
                isSearchable={false}
                options={personTypeOptions}
                placeholder="Selecione"
                value={
                  formik.values.person_type === undefined || formik.values.person_type === null
                    ? undefined
                    : {
                        value: formik.values.person_type.value,
                        label: formik.values.person_type.description,
                      }
                }
                onChange={(item) => {
                  formik.setFieldValue('person_type', {
                    value: item?.value,
                    description: item?.label,
                  });
                  if (item?.value === 'PHYSICAL') {
                    formik.setFieldValue('cnpj', '');
                  } else {
                    formik.setFieldValue('cpf', '');
                  }
                }}
              />
            </div>
            <div className="col-12 col-sm-4">
              {formik.values.person_type?.value === 'PHYSICAL' ? (
                <>
                  <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">CPF</h3>
                  <CpfInput
                    className="form-control"
                    id="cpf"
                    placeholder="xxx.xxx.xxx-xx"
                    {...formik.getFieldProps('cpf')}
                  />
                  {!!formik.errors.cpf && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">{formik.errors.cpf}</div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">CNPJ</h3>
                  <CnpjInput
                    className="form-control"
                    id="cnpj"
                    placeholder="xx.xxx.xxx/xxxx-xx"
                    {...formik.getFieldProps('cnpj')}
                  />
                  {!!formik.errors.cnpj && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">{formik.errors.cnpj}</div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Regime tributário</h3>
              <Select
                className="react-select-styled"
                classNamePrefix="react-select"
                id="tax_regime"
                isSearchable={false}
                name="tax_regime"
                options={taxRegimeOptions}
                placeholder="Selecionar"
                value={
                  formik.values.tax_regime === undefined || formik.values.tax_regime === null
                    ? undefined
                    : {
                        value: formik.values.tax_regime.value,
                        label: formik.values.tax_regime.description,
                      }
                }
                onChange={(item) => {
                  formik.setFieldValue('tax_regime', {
                    value: item?.value,
                    description: item?.label,
                  });
                  if (!usesIncomeTaxRegime(item?.value)) {
                    formik.setFieldValue('income_tax_regime', undefined);
                  }
                }}
              />
              {!!formik.errors.tax_regime && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.tax_regime}</div>
                </div>
              )}
            </div>
            {usesIncomeTaxRegime(formik.values.tax_regime?.value) && (
              <div className="col-12 col-sm-6 col-md-3">
                <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Apuração do IRPJ</h3>
                <Select
                  className="react-select-styled"
                  classNamePrefix="react-select"
                  id="income_tax_regime"
                  isClearable={true}
                  isSearchable={false}
                  name="income_tax_regime"
                  options={incomeTaxRegimeOptions}
                  placeholder="Selecionar"
                  value={
                    formik.values.income_tax_regime
                      ? {
                          value: formik.values.income_tax_regime.value,
                          label: formik.values.income_tax_regime.description,
                        }
                      : null
                  }
                  onChange={(item) => {
                    formik.setFieldValue(
                      'income_tax_regime',
                      item ? { value: item.value, description: item.label } : undefined,
                    );
                  }}
                />
              </div>
            )}
            <div className="col-12 col-sm-6 col-md-3">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Inscrição municipal</h3>
              <input
                className="form-control"
                id="municipal_registration"
                placeholder="Inscrição municipal"
                type="text"
                {...formik.getFieldProps('municipal_registration')}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Inscrição estadual</h3>
              <input
                className="form-control"
                id="state_registration"
                placeholder="Inscrição estadual"
                type="text"
                {...formik.getFieldProps('state_registration')}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Número de funcionários</h3>
              <NumericFormat
                allowNegative={false}
                className="form-control mt-1"
                decimalScale={0}
                fixedDecimalScale={true}
                id="number_of_employees"
                placeholder="Número de funcionários"
                onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                {...formik.getFieldProps('number_of_employees')}
              />
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Telefone</h3>
              <PatternFormat
                className="form-control"
                format="(##) #####-####"
                id="telephone"
                placeholder="(xx) xxxxx-xxxx"
                {...formik.getFieldProps('telephone')}
              />
              {!!formik.errors.telephone && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.telephone}</div>
                </div>
              )}
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">E-mail</h3>
              <input
                className="form-control"
                id="email"
                placeholder="E-mail"
                type="text"
                {...formik.getFieldProps('email')}
              />
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Website</h3>
              <input
                className="form-control"
                id="website"
                placeholder="Website"
                type="text"
                {...formik.getFieldProps('website')}
              />
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Whatsapp</h3>
              <PatternFormat
                className="form-control"
                format="(##) #####-####"
                id="whatsapp"
                placeholder="(xx) xxxxx-xxxx"
                {...formik.getFieldProps('whatsapp')}
              />
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Instagram</h3>
              <input
                className="form-control"
                id="instagram"
                placeholder="Instagram"
                type="text"
                {...formik.getFieldProps('instagram')}
              />
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Facebook</h3>
              <input
                className="form-control"
                id="facebook"
                placeholder="Facebook"
                type="text"
                {...formik.getFieldProps('facebook')}
              />
            </div>
          </div>
          <div className="row g-6 mb-2">
            <h3 className="stepper-title me-7">Endereço</h3>
            <div className="col-12 col-sm-3">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">CEP</h3>
              <PatternFormat
                className="form-control"
                format="#####-###"
                id="zip_code"
                placeholder="xxxxx-xxx"
                {...formik.getFieldProps('address.zip_code')}
              />
              {getIn(formik.touched, 'address.zip_code') && getIn(formik.errors, 'address.zip_code') && (
                <div className="fv-plugins-message-container invalid-feedback">
                  {getIn(formik.errors, 'address.zip_code')}
                </div>
              )}
            </div>
            <div className="col-12 col-sm-6">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Rua</h3>
              <input
                className="form-control"
                id="street"
                placeholder="Rua"
                type="text"
                {...formik.getFieldProps('address.street')}
              />
              {getIn(formik.touched, 'address.street') && getIn(formik.errors, 'address.street') && (
                <div className="fv-plugins-message-container invalid-feedback">
                  {getIn(formik.errors, 'address.street')}
                </div>
              )}
            </div>
            <div className="col-12 col-sm-3">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Número</h3>
              <input
                className="form-control"
                id="number"
                placeholder="Número"
                type="text"
                {...formik.getFieldProps('address.number')}
              />
              {getIn(formik.touched, 'address.number') && getIn(formik.errors, 'address.number') && (
                <div className="fv-plugins-message-container invalid-feedback">
                  {getIn(formik.errors, 'address.number')}
                </div>
              )}
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Bairro</h3>
              <input
                className="form-control"
                id="neighborhood"
                placeholder="Bairro"
                type="text"
                {...formik.getFieldProps('address.neighborhood')}
              />
              {getIn(formik.touched, 'address.neighborhood') && getIn(formik.errors, 'address.neighborhood') && (
                <div className="fv-plugins-message-container invalid-feedback">
                  {getIn(formik.errors, 'address.neighborhood')}
                </div>
              )}
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate">Complemento</h3>
              <input
                className="form-control"
                id="complement"
                placeholder="Complemento"
                type="text"
                {...formik.getFieldProps('address.complement')}
              />
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Cidade</h3>
              <input
                className="form-control"
                id="city"
                placeholder="Cidade"
                type="text"
                {...formik.getFieldProps('address.city')}
              />
              {getIn(formik.touched, 'address.city') && getIn(formik.errors, 'address.city') && (
                <div className="fv-plugins-message-container invalid-feedback">
                  {getIn(formik.errors, 'address.city')}
                </div>
              )}
            </div>
            <div className="col-12 col-sm-4">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">UF</h3>
              <Select
                className="react-select-styled my-3"
                classNamePrefix="react-select"
                id="state"
                isSearchable={true}
                menuPlacement="top"
                name="state"
                options={BRAZILIAN_STATES}
                placeholder="Selecione"
                value={
                  formik.values.address?.state === undefined || formik.values.address.state === null
                    ? null
                    : {
                        value: formik.values.address.state.value,
                        label: formik.values.address.state.description,
                      }
                }
                onChange={(item) => {
                  formik.setFieldValue('address.state', {
                    value: item?.value,
                    description: item?.label,
                  });
                }}
              />
              {getIn(formik.touched, 'address.state') && getIn(formik.errors, 'address.state') && (
                <div className="fv-plugins-message-container invalid-feedback">
                  {getIn(formik.errors, 'address.state')}
                </div>
              )}
            </div>
          </div>
        </div>
      </MetronicCard>
    </div>
  );
};

export default Step1;
